// How much of the screen the software keyboard is covering, and whether the field
// somebody is typing into is underneath it.
//
// Nothing in this app knew the keyboard existed, which broke three things on a
// phone: the chat composer sat under it, a field low on an editor form could not
// be scrolled up to (its scroll container never got shorter), and every dialog is
// centred in the LAYOUT viewport — which does not change when the keyboard opens —
// so eight-plus dialogs with text inputs were centred behind it.
//
// PURE — no DOM access, no imports — so tests/… can exercise the arithmetic under
// jsdom, which implements neither a keyboard nor VisualViewport. The effects that
// read the real viewport live in hooks/useKeyboardAware.ts.

/**
 * Below this, a shrunken visual viewport is NOT treated as a keyboard.
 *
 * The signal is noisy: in a browser tab a collapsing address bar leaves
 * `window.innerHeight` (the layout viewport) permanently larger than
 * `visualViewport.height` by 60-100px with no keyboard anywhere, and shrinking the
 * app by that would be a bug in the ordinary case to fix a rare one. Every phone
 * software keyboard is well over 200px tall, so 150 sits clear of the noise and
 * clear of the real thing.
 *
 * The cost is that an iOS accessory bar alone (~44px, only with a hardware
 * keyboard attached) reads as no keyboard. That is the right trade: it covers one
 * row of content, against wrongly shrinking the app for every browser-tab user.
 */
export const KEYBOARD_MIN_INSET = 150

/**
 * The keyboard's height, from the visual viewport's geometry.
 *
 * `offsetTop` is load-bearing rather than defensive: iOS does not resize the
 * visual viewport so much as PUSH it up the layout viewport, so the space taken
 * from the bottom is what is left after both the height and the offset are
 * accounted for. Ignoring it under-reports the inset on exactly the platform that
 * needs it most.
 *
 * Returns 0 for anything below the threshold, and never a negative number —
 * `innerHeight` can legitimately be the SMALLER of the two mid-rotation.
 */
export function computeKeyboardInset(
  innerHeight: number,
  viewportHeight: number,
  offsetTop = 0,
): number {
  if (!Number.isFinite(innerHeight) || !Number.isFinite(viewportHeight)) return 0
  const covered = innerHeight - (viewportHeight + (Number.isFinite(offsetTop) ? offsetTop : 0))
  if (!Number.isFinite(covered) || covered < KEYBOARD_MIN_INSET) return 0
  return Math.round(covered)
}

/** Breathing room kept between a focused field and the top of the keyboard. */
export const OBSCURED_MARGIN = 12

/**
 * Whether a focused field needs scrolling up to be readable.
 *
 * Deliberately a test rather than an unconditional scroll: scrolling every field
 * on focus yanks the page about even when the field was already perfectly
 * visible, and a chat composer pinned to the bottom of its own scroller would be
 * dragged into the middle of the thread for no reason.
 *
 * Both bounds are in LAYOUT-viewport coordinates, which is what
 * getBoundingClientRect returns — hence the caller adding `offsetTop` to the
 * visual viewport's height to get `visibleBottom`.
 */
export function isObscured(
  rectBottom: number,
  visibleBottom: number,
  margin = OBSCURED_MARGIN,
): boolean {
  if (!Number.isFinite(rectBottom) || !Number.isFinite(visibleBottom)) return false
  return rectBottom > visibleBottom - margin
}

/**
 * Whether focusing this element should have raised a keyboard.
 *
 * Checked by tag and type rather than by "is it focusable", because a button, a
 * link and a scroll container all take focus without a keyboard appearing, and
 * scrolling the page under someone who merely tabbed to a button is worse than
 * doing nothing. `contenteditable` is included — the chat composer could become
 * one — and the non-text input types are excluded because they raise a picker or
 * nothing at all.
 */
const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range',
  'reset', 'submit',
])

export function isTextEntry(
  tagName: string | undefined,
  inputType?: string | null,
  isContentEditable = false,
): boolean {
  if (isContentEditable) return true
  const tag = (tagName ?? '').toLowerCase()
  if (tag === 'textarea' || tag === 'select') return true
  if (tag !== 'input') return false
  return !NON_TEXT_INPUT_TYPES.has((inputType ?? 'text').toLowerCase())
}

/** The CSS custom property every keyboard-aware rule reads. */
export const KEYBOARD_INSET_VAR = '--keyboard-inset'
